import React, { useState, useMemo, useEffect } from 'react';
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import {
  BlogHeader,
  BlogSearch,
  BlogCategoryFilter,
  BlogGrid
} from "@/components/blog";
import {
  blogPosts,
  blogCategories,
  BlogCategory
} from "@/data/blogPosts";

const Blog: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<BlogCategory | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Calculate post counts for each category
  const postCounts = useMemo(() => {
    const counts: Record<BlogCategory | 'all', number> = {
      all: blogPosts.length,
      'Features': 0,
      'Guides & Tips': 0,
      'Updates': 0
    };

    blogPosts.forEach(post => {
      counts[post.category]++;
    });

    return counts;
  }, []);

  // Filter posts based on category and search
  const filteredPosts = useMemo(() => {
    let posts = [...blogPosts];

    // Filter by category
    if (selectedCategory) {
      posts = posts.filter(post => post.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      posts = posts.filter(post =>
        post.title.toLowerCase().includes(query) ||
        post.excerpt.toLowerCase().includes(query) ||
        post.tags.some(tag => tag.toLowerCase().includes(query)) ||
        post.author.toLowerCase().includes(query)
      );
    }

    // Sort by date (newest first)
    posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return posts;
  }, [selectedCategory, searchQuery]);

  // Determine if we should show featured post (only when no filters active)
  const showFeatured = !selectedCategory && !searchQuery && filteredPosts.length > 0;

  // Set page title and meta description
  useEffect(() => {
    document.title = 'Blog | Tag-Inn';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Insights, guides, and updates to help you run your business better. Explore articles on features, tips, and company news.');
    }
  }, []);

  return (
    <div className="min-h-screen w-full flex flex-col bg-background">
      <Navbar />

      <main className="flex-grow">
        {/* Header section */}
        <BlogHeader
          title="Blog"
          subtitle="Insights, guides, and updates to help you run your business better."
        />

        {/* Content section */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12 md:py-16">
          {/* Filters bar */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
            {/* Category filter */}
            <BlogCategoryFilter
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              postCounts={postCounts}
            />

            {/* Search */}
            <BlogSearch
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search articles..."
            />
          </div>

          {/* Results indicator */}
          {(selectedCategory || searchQuery) && (
            <div className="mb-8 pb-6 border-b border-border">
              <p className="text-sm text-muted-foreground">
                Showing {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'}
                {selectedCategory && (
                  <span> in <span className="font-medium text-foreground">{selectedCategory}</span></span>
                )}
                {searchQuery && (
                  <span> matching "<span className="font-medium text-foreground">{searchQuery}</span>"</span>
                )}
              </p>
            </div>
          )}

          {/* Posts grid */}
          <BlogGrid
            posts={filteredPosts}
            showFeatured={showFeatured}
            emptyMessage={
              searchQuery
                ? "Try adjusting your search terms or browse by category."
                : "No articles in this category yet. Check back soon!"
            }
          />

          {/* Load more placeholder - for future pagination */}
          {filteredPosts.length > 6 && (
            <div className="mt-16 text-center">
              <button className="inline-flex items-center justify-center px-8 py-3 text-sm font-semibold text-muted-foreground bg-sidebar hover:bg-sidebar/80 rounded-full transition-all duration-300">
                Load more articles
              </button>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;
